import {
  createContext,
  useContext,
  useState,
  type PropsWithChildren,
  type ComponentType,
  forwardRef,
} from 'react'
import { RootStore } from './RootStore'
import type { AddressStore } from './addressStore/AddressStore'
import type { DiscountStore } from './discountStore/DiscountStore'
import type { PhotoBankStore } from './photoBankStore/PhotoBankStore'
import type { PhotoBooksStore } from './photoBooksStore/PhotoBooksStore'
import type { TilesStore } from './tilesStore/TilesStore'

const RootStoreContext = createContext<RootStore | null>(null)

let rootStore: RootStore | null = null

export function getRootStore(): RootStore {
  if (!rootStore) {
    throw new Error('RootStore not initialized')
  }
  return rootStore
}

export function getAddressStore(): AddressStore {
  return getRootStore().addressStore
}

/** @public */
export function getDiscountStore(): DiscountStore {
  return getRootStore().discountStore
}

export function getPhotoBankStore(): PhotoBankStore {
  return getRootStore().photoBankStore
}

export function getPhotoBooksStore(): PhotoBooksStore {
  return getRootStore().photoBooksStore
}

export function RootStoreProvider({ children }: PropsWithChildren) {
  const [store] = useState(() => {
    if (!rootStore) {
      rootStore = new RootStore()
    }
    return rootStore
  })

  return (
    <RootStoreContext.Provider value={store}>
      {children}
    </RootStoreContext.Provider>
  )
}

export function useRootStore(): RootStore {
  const store = useContext(RootStoreContext)
  if (!store) {
    throw new Error('useRootStore must be used within RootStoreProvider')
  }
  return store
}

export function useAddressStore(): AddressStore {
  return useRootStore().addressStore
}

/** @public */
export function useDiscountStore(): DiscountStore {
  return useRootStore().discountStore
}

export function getTilesStore(): TilesStore {
  return getRootStore().tilesStore
}

export function usePhotoBankStore(): PhotoBankStore {
  return useRootStore().photoBankStore
}

export function usePhotoBooksStore(): PhotoBooksStore {
  return useRootStore().photoBooksStore
}

export function useTilesStore(): TilesStore {
  return useRootStore().tilesStore
}

export interface WithAddressStoreProps {
  addressStore: AddressStore
}

export interface WithPhotoBankStoreProps {
  photoBankStore: PhotoBankStore
}

export function withAddressStore<P extends WithAddressStoreProps>(
  WrappedComponent: ComponentType<P>
) {
  const WithAddressStoreComponent = forwardRef<
    unknown,
    Omit<P, keyof WithAddressStoreProps>
  >((props, ref) => {
    const addressStore = useAddressStore()
    return (
      <WrappedComponent
        ref={ref}
        {...(props as unknown as P)}
        addressStore={addressStore}
      />
    )
  })

  WithAddressStoreComponent.displayName = `withAddressStore(${
    WrappedComponent.displayName || WrappedComponent.name || 'Component'
  })`

  return WithAddressStoreComponent
}

export function withPhotoBankStore<P extends WithPhotoBankStoreProps>(
  WrappedComponent: ComponentType<P>
) {
  const WithPhotoBankStoreComponent = forwardRef<
    unknown,
    Omit<P, keyof WithPhotoBankStoreProps>
  >((props, ref) => {
    const photoBankStore = usePhotoBankStore()
    return (
      <WrappedComponent
        ref={ref}
        {...(props as unknown as P)}
        photoBankStore={photoBankStore}
      />
    )
  })

  WithPhotoBankStoreComponent.displayName = `withPhotoBankStore(${
    WrappedComponent.displayName || WrappedComponent.name || 'Component'
  })`

  return WithPhotoBankStoreComponent
}
